import { useEffect, useRef, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import {
  fetchPropertyMappingReview,
  updatePropertyMappingStatus,
} from '../services/propertyMappingService'
import type { PropertyMappingRow, PropertyMatchCandidate } from '../types'

interface PropertyMappingActionDialogProps {
  row: PropertyMappingRow | null
  onClose: () => void
  onSaved: () => void
}

function MatchPill({ matched, label }: { matched: boolean; label: string }) {
  return (
    <span
      className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
        matched
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-slate-50 text-slate-500'
      }`}
    >
      {label}: {matched ? 'match' : 'different'}
    </span>
  )
}

export function PropertyMappingActionDialog({
  row,
  onClose,
  onSaved,
}: PropertyMappingActionDialogProps) {
  const [candidates, setCandidates] = useState<PropertyMatchCandidate[]>([])
  const [search, setSearch] = useState('')
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const requestId = useRef(0)

  useEffect(() => {
    if (!row) return
    setSearch('')
    setNote(row.adminNote || '')
    setSelectedPropertyId(row.libertyPropertyId || null)
  }, [row])

  useEffect(() => {
    if (!row) return
    const currentRequest = ++requestId.current
    const timer = window.setTimeout(() => {
      setLoading(true)
      void fetchPropertyMappingReview(row.id, search.trim())
        .then((result) => {
          if (currentRequest !== requestId.current) return
          setCandidates(result.candidates)
          setSelectedPropertyId((current) =>
            current || (result.candidates.length === 1 ? result.candidates[0].propertyId : null),
          )
        })
        .catch(showApiError)
        .finally(() => {
          if (currentRequest === requestId.current) setLoading(false)
        })
    }, search ? 300 : 0)

    return () => window.clearTimeout(timer)
  }, [row, search])

  if (!row) return null

  const isSkipped = row.status === 'SKIPPED'

  const saveManualMap = async () => {
    if (!selectedPropertyId) return
    setSaving(true)
    try {
      await updatePropertyMappingStatus(row.id, {
        status: 'MAPPED',
        property_id: selectedPropertyId,
        note: note.trim() || undefined,
      })
      showSuccess(`${row.sourcePropertyName} was manually mapped.`)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  const saveSkip = async () => {
    const nextStatus = isSkipped ? 'NEEDS_REVIEW' : 'SKIPPED'
    if (
      !window.confirm(
        isSkipped
          ? `Unmark skip for ${row.sourcePropertyName}? It will go back to needs review.`
          : `Skip ${row.sourcePropertyName} from property matching?`,
      )
    ) {
      return
    }
    setSaving(true)
    try {
      await updatePropertyMappingStatus(row.id, {
        status: nextStatus,
        note: note.trim() || undefined,
      })
      showSuccess(
        isSkipped
          ? `${row.sourcePropertyName} was unmarked from skip.`
          : `${row.sourcePropertyName} was marked as skipped.`,
      )
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/40 p-0 backdrop-blur-[1px] sm:items-center sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="property-mapping-dialog-title"
        className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4 sm:px-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-600">
              Yardi property {row.sourcePropertyCode}
            </div>
            <h2 id="property-mapping-dialog-title" className="mt-1 text-xl font-bold text-slate-900">
              Update property mapping status
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 sm:p-6">
          <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-4">
            <div className="grid gap-4 md:grid-cols-[1.2fr_0.7fr_1.4fr]">
              <div>
                <div className="text-xs text-slate-500">PMS property name</div>
                <div className="mt-0.5 font-semibold text-slate-900">{row.sourcePropertyName}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">PMS code</div>
                <div className="mt-0.5 font-semibold text-slate-900">{row.sourcePropertyCode}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Current decision</div>
                <div className="mt-0.5 font-semibold text-slate-900">
                  {row.status === 'SKIPPED'
                    ? 'Skipped'
                    : row.status === 'MAPPED'
                      ? 'Mapped'
                      : 'Needs review'}
                </div>
                {row.matchReason ? <div className="mt-1 text-xs text-slate-500">{row.matchReason}</div> : null}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Liberty Rent property candidates</h3>
                <p className="text-sm text-slate-500">
                  Automatic mapping requires exactly one Liberty property with the same normalized name.
                </p>
              </div>
              <label className="relative block sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search another LR property"
                  className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </label>
            </div>

            <div className="mt-3 space-y-3">
              {loading ? (
                <div className="py-9 text-center text-sm text-slate-500">
                  <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                  Loading candidates…
                </div>
              ) : candidates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-9 text-center text-sm text-slate-500">
                  No candidate found. Search by property name, address or Liberty property ID.
                </div>
              ) : (
                candidates.map((candidate) => {
                  const selected = selectedPropertyId === candidate.propertyId
                  return (
                    <button
                      key={candidate.propertyId}
                      type="button"
                      onClick={() => setSelectedPropertyId(candidate.propertyId)}
                      className={`w-full rounded-xl border p-4 text-left ${
                        selected
                          ? 'border-sky-500 bg-sky-50/50 ring-2 ring-sky-100'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <div className="font-semibold text-slate-900">
                            {candidate.propertyName}
                            <span className="ml-2 text-xs font-medium text-slate-400">
                              #{candidate.propertyId}
                            </span>
                          </div>
                          <div className="mt-1 text-sm text-slate-600">{candidate.address || 'No address'}</div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <MatchPill label="Name" matched={Boolean(candidate.flags?.nameExact || candidate.nameMatch)} />
                          <MatchPill label="Address" matched={Boolean(candidate.flags?.addressExact)} />
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                            Score {candidate.score}/160
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })
              )}
            </div>
          </div>

          <label className="mt-5 block text-sm font-medium text-slate-700">
            Review note
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              placeholder="Optional reason or review note"
              className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            />
          </label>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Close
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void saveSkip()}
            className="h-10 rounded-lg border border-rose-300 bg-white px-4 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            {isSkipped ? 'Unmark Skip' : 'Mark Skip'}
          </button>
          <button
            type="button"
            disabled={saving || !selectedPropertyId}
            onClick={() => void saveManualMap()}
            className="h-10 rounded-lg bg-sky-500 px-5 text-sm font-semibold text-white hover:bg-sky-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Map selected property'}
          </button>
        </div>
      </div>
    </div>
  )
}
